import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { GradientButton } from '@/components/ui/GradientButton';
import { 
  Mail,
  User,
  Shield,
  Lock,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { sendRequest } from '@/api';
import FeatureExplainer from './FeatureExplainer';
import { useAnalytics } from './AnalyticsProvider';

const EmailCapture = ({ onComplete }) => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsSubmitting(true);

    if (!email.trim() || !firstName.trim()) {
      setErrorMessage("Please fill in all fields");
      setIsSubmitting(false);
      return;
    }

    try {
      await sendRequest("/users", "PUT", { email, name: firstName });
      trackEvent('signup_complete', 'onboarding', 'email_capture');
      setShowFeatures(true);
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showFeatures) {
    return <FeatureExplainer onContinue={() => navigate('/')} />;
  }

  return (
    <div className="h-screen w-screen overflow-hidden relative">
      <style>{`
        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        .animated-gradient {
          background: linear-gradient(
            -45deg,
            #1a0f2e,
            #2d1b4e,
            #432a75,
            #31204f,
            #1f1235
          );
          background-size: 400% 400%;
          animation: gradient 15s ease infinite;
        }

        .glow {
          position: absolute;
          width: 40%;
          height: 40%;
          filter: blur(100px);
          border-radius: 50%;
        }

        .glow-1 {
          top: -10%;
          left: -10%;
          background: rgba(123, 31, 162, 0.5);
          animation: move1 10s infinite alternate;
        }

        .glow-2 {
          bottom: -10%;
          right: -10%;
          background: rgba(103, 58, 183, 0.5);
          animation: move2 15s infinite alternate;
        }

        @keyframes move1 {
          0% { transform: translate(0, 0); }
          100% { transform: translate(10%, 10%); }
        }

        @keyframes move2 {
          0% { transform: translate(0, 0); }
          100% { transform: translate(-10%, -10%); }
        }
      `}</style>

      <div className="animated-gradient absolute inset-0">
        <div className="glow glow-1" />
        <div className="glow glow-2" />
      </div>

      <div className="relative h-full w-full">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 -translate-y-12 translate-x-12 blur-3xl opacity-20 w-96 h-96 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full" />
        <div className="absolute bottom-0 left-0 translate-y-12 -translate-x-12 blur-3xl opacity-20 w-96 h-96 bg-gradient-to-tr from-pink-500 to-purple-500 rounded-full" />
        
        {/* Content */}
        <div className="relative h-full w-full flex flex-col items-center justify-center p-4">
          <div className="w-full max-w-md text-center space-y-8">
            <div className="space-y-4">
              <Badge className="bg-gradient-to-r from-indigo-500 to-pink-500 mb-4 rounded-full animate-bounce">
                <span className="flex items-center gap-1">
                  <span>✨</span> Almost There <span>✨</span>
                </span>
              </Badge>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-300 via-purple-300 to-pink-300 text-transparent bg-clip-text">
                Hey Future Star! 🌟
              </h1>
              <p className="text-gray-300 text-lg">
                Let's get your creator journey started 🚀
              </p>
            </div>

            <Card className="p-8 bg-white/[0.03] backdrop-blur-xl border-white/[0.05] hover:border-purple-500/50 transition-all duration-300 rounded-2xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  {/* First Name Input */}
                  <div className="space-y-2">
                    <div className="relative group">
                      <User className="h-4 w-4 absolute left-3 top-3 text-gray-400 group-hover:text-purple-400 transition-colors" />
                      <Input
                        type="text"
                        placeholder="What should we call you? 😊"
                        required
                        className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-400 w-full focus:border-purple-500/50 transition-all rounded-xl"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Email Input */}
                  <div className="space-y-2">
                    <div className="relative group">
                      <Mail className="h-4 w-4 absolute left-3 top-3 text-gray-400 group-hover:text-purple-400 transition-colors" />
                      <Input
                        type="email"
                        placeholder="Your awesome email 📧"
                        required
                        className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-400 w-full focus:border-purple-500/50 transition-all rounded-xl"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <p className="text-xs text-left text-gray-400 flex items-center gap-2">
                      <Sparkles className="h-3 w-3" />
                      Where we'll send your magic download links ✨
                    </p>
                  </div>

                  {errorMessage && (
                    <p className="text-sm text-red-400 text-center">
                      {errorMessage}
                    </p>
                  )}
                </div>

                <GradientButton 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 shadow-xl hover:shadow-purple-500/20 transition-all duration-300 hover:-translate-y-0.5 rounded-xl"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin">🔮</span> Making Magic...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Start Creating 
                      <span className="group-hover:translate-x-1 transition-transform">🔥</span>
                    </span>
                  )}
                </GradientButton>
              </form>

              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="flex items-center gap-3 text-left">
                  <Shield className="h-5 w-5 text-purple-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-white font-medium">Trust & Safety 🛡️</p>
                    <p className="text-xs text-gray-400">
                      Your data is safe with us, pinky promise! 🤙
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            <p className="text-xs text-gray-400 flex items-center justify-center gap-2">
              <Lock className="h-3 w-3" />
              Protected by industry-leading encryption 🔒
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailCapture;